if (!customElements.get('engraving-form')) {
  customElements.define(
    'engraving-form',
    class EngravingForm extends HTMLElement {
      constructor() {
        super();

        this.outsideSection = this.querySelector('[data-engraving="Outside"]');
        this.insideSection = this.querySelector('[data-engraving="Inside"]');
        this.textareas = this.querySelectorAll('.engraving-form__textarea');
        this.selects = this.querySelectorAll('.engraving-form__select');

        this.debounceTimer = null;
        this.fontMap = {
          'Elvish': '"Elvish", "Tengwar Annatar", fantasy',
          'Kereru': '"Kereru", sans-serif',
          'Kereru Italic': '"Kereru", sans-serif',
          'Bookman Old Style': '"Bookman Old Style", serif',
          'Colonna MT': '"Colonna MT", serif',
          'Edwardian Script': '"Edwardian Script ITC", cursive',
          'Lucida Handwriting': '"Lucida Handwriting", cursive',
          'Lucida Calligraphy': '"Lucida Calligraphy", cursive',
          'Apple Chancery': '"Apple Chancery", cursive',
          'Old English': '"Old English Text MT", serif',
          'Script Bold': '"Script MT Bold", cursive',
          'Typewriter': '"Courier New", monospace',
        };

        this.bindEvents();
        this.initFromVariant();
        this.initFitIndicators();
      }

      bindEvents() {
        // Listen to variant picker's Engraving option changes
        this.engravingOptions = document.querySelectorAll('[name="options[Engraving]"]');
        this.engravingOptions.forEach((opt) => {
          opt.addEventListener('change', (e) => this.switchEngraving(e.target.value));
        });

        // Text input listeners (debounced)
        this.textareas.forEach((textarea) => {
          textarea.addEventListener('input', this.handleTextInput.bind(this));
        });

        // Font select listeners
        this.selects.forEach((select) => {
          select.addEventListener('change', this.handleFontChange.bind(this));
        });
      }

      initFromVariant() {
        // On page load, find the currently selected Engraving variant option
        const checked = document.querySelector('[name="options[Engraving]"]:checked');
        if (checked) {
          this.switchEngraving(checked.value);
        }
      }

      switchEngraving(option) {
        const upper = option.toUpperCase();
        const showOutside = upper.includes('OUTSIDE') || upper.includes('BOTH');
        const showInside = upper.includes('INSIDE') || upper.includes('BOTH');

        // Hide all first
        this.hideSection(this.outsideSection, 'outside');
        this.hideSection(this.insideSection, 'inside');

        // Show relevant sections
        if (showOutside) this.showSection(this.outsideSection, 'outside');
        if (showInside) this.showSection(this.insideSection, 'inside');
      }

      hideSection(section, side) {
        if (!section) return;
        section.classList.add('hide');
        section.querySelectorAll('select, textarea').forEach((el) => {
          el.disabled = true;
        });
        this.dispatchEngravingUpdate(side, '', '');
      }

      showSection(section, side) {
        if (!section) return;
        section.classList.remove('hide');
        section.querySelectorAll('select, textarea').forEach((el) => {
          el.disabled = false;
        });

        const textarea = section.querySelector('.engraving-form__textarea');
        const select = section.querySelector('.engraving-form__select');
        const font = select ? select.value : '';

        // Pre-fill default message if configured
        if (textarea && textarea.dataset.displayDefaultMessage === 'true' && !textarea.dataset.defaultApplied) {
          textarea.dataset.defaultApplied = 'true';
          // Value is already set from Liquid template
        }

        // Update font preview and translation
        if (textarea) {
          this.updateFontPreview(side, textarea.value, font);
          this.updateTranslation(side, textarea.value, font);
        }

        // Send current text/font to 3D viewer
        if (textarea && textarea.value) {
          this.dispatchEngravingUpdate(side, textarea.value, font);
        }
      }

      handleTextInput(event) {
        const side = event.target.dataset.side;
        const text = event.target.value;
        const fontSelect = this.querySelector(`.engraving-form__select[data-side="${side}"]`);
        const font = fontSelect ? fontSelect.value : '';

        // Update font preview and translation immediately
        this.updateFontPreview(side, text, font);
        this.updateTranslation(side, text, font);

        // Bug fix #10 — 3D viewer engraving rebuild is the biggest cost while
        // typing. 500ms felt laggy because it ran after the user already
        // stopped; 150ms keeps the preview tight without rebuilding mid-burst.
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.dispatchEngravingUpdate(side, text, font);
        }, 150);

        // Bug fix #10 — also debounce the fit indicator DOM updates; the
        // engine itself is cheap but the per-keystroke reflow accumulates.
        if (!this._fitTimers) this._fitTimers = {};
        clearTimeout(this._fitTimers[side]);
        this._fitTimers[side] = setTimeout(() => {
          this.updateFitIndicator(side, text, font);
        }, 100);
      }

      handleFontChange(event) {
        const side = event.target.dataset.side;
        const font = event.target.value;
        const textarea = this.querySelector(`.engraving-form__textarea[data-side="${side}"]`);
        const text = textarea ? textarea.value : '';

        // Update font preview and translation
        this.updateFontPreview(side, text, font);
        this.updateTranslation(side, text, font);

        this.dispatchEngravingUpdate(side, text, font);
        this.updateFitIndicator(side, text, font);
      }

      updateFontPreview(side, text, font) {
        const previewEl = this.querySelector(`.engraving-form__font-preview[data-display-fonts-output="${side}"]`);
        if (!previewEl) return;

        if (font === 'Elvish') {
          // Hide font preview for Elvish (translation div shows instead)
          previewEl.style.display = 'none';
          previewEl.textContent = '';
        } else if (text && text.trim()) {
          previewEl.style.display = '';
          previewEl.textContent = text;
          previewEl.style.fontFamily = this.fontMap[font] || 'serif';
          if (font === 'Kereru Italic') {
            previewEl.style.fontStyle = 'italic';
          } else {
            previewEl.style.fontStyle = '';
          }
        } else {
          previewEl.style.display = 'none';
          previewEl.textContent = '';
        }
      }

      /**
       * transcribe() emits `<abbr class="error" title="…">.</abbr>` for any
       * character it can't map (ö, ş, non-Latin diacritics, etc.). The
       * canonical translator page renders that via innerHTML so the
       * `<abbr[title]>` browser-default `text-decoration: underline dotted`
       * decorates the placeholder dot — matching the small tehta-like
       * mark customers expect. Rebuild each `<abbr>` as a real element so
       * the underline survives without exposing us to raw innerHTML.
       */
      // The transliteration separates lines with <br>; the app writes its output as
      // HTML, so each line of a verse sits on its own row. Written as text, the
      // markers showed up as extra Tengwar glyphs and the lines ran together
      // (RSFY-826). Real line breaks, still no HTML injection.
      _appendTextWithBreaks(container, text) {
        const parts = String(text).split(/<br\s*\/?>/i);
        parts.forEach((part, i) => {
          if (i > 0) container.appendChild(document.createElement('br'));
          if (part) container.appendChild(document.createTextNode(part));
        });
      }

      _renderTranscribedTextInto(container, str) {
        if (!container) return;
        container.textContent = '';
        if (!str || typeof str !== 'string') {
          if (str) container.textContent = String(str);
          return;
        }
        const re = /<abbr\b([^>]*)>([\s\S]*?)<\/abbr>/gi;
        let lastIndex = 0;
        let m;
        while ((m = re.exec(str)) !== null) {
          if (m.index > lastIndex) {
            this._appendTextWithBreaks(container, this._decodeHtmlEntities(str.slice(lastIndex, m.index)));
          }
          const attrs = m[1] || '';
          const inner = m[2] || '';
          const abbr = document.createElement('abbr');
          const titleMatch = attrs.match(/title=("([^"]*)"|'([^']*)')/i);
          if (titleMatch) {
            abbr.title = this._decodeHtmlEntities(titleMatch[2] || titleMatch[3] || '');
          }
          if (/class=("error"|'error')/i.test(attrs)) {
            abbr.className = 'engraving-form__elvish-error';
          }
          abbr.textContent = this._decodeHtmlEntities(inner);
          container.appendChild(abbr);
          lastIndex = m.index + m[0].length;
        }
        if (lastIndex < str.length) {
          this._appendTextWithBreaks(container, this._decodeHtmlEntities(str.slice(lastIndex)));
        }
      }

      _decodeHtmlEntities(str) {
        if (str == null) return '';
        return String(str)
          .replace(/&quot;/g, '"')
          .replace(/&#34;/g, '"')
          .replace(/&apos;/g, "'")
          .replace(/&#39;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&');
      }

      _stripElvishErrorMarkup(str) {
        if (!str || typeof str !== 'string') return str || '';
        let cleaned = str.replace(/<abbr\b[^>]*>([\s\S]*?)<\/abbr>/gi, '$1');
        cleaned = cleaned.replace(/<\/?[a-z][^>]*>/gi, '');
        return this._decodeHtmlEntities(cleaned);
      }

      updateTranslation(side, text, font) {
        const translationEl = this.querySelector(`.engraving-form__translation[data-translation="${side}"]`);
        if (!translationEl) return;

        if (font === 'Elvish' && text && text.trim()) {
          let tengwarText = text;
          if (typeof window.transcribe === 'function') {
            try {
              // Match the canonical translator (jenshansen.com/.../elvish-translator)
              // so the PDP preview, the ring builder, and the website agree on
              // every tehtar combination.
              tengwarText = window.transcribe(text, {
                noAchLaut: true,
                medialOre: true,
                language: 'english',
              });
            } catch (e) {
              // Fallback: show original text with Tengwar font
            }
          }
          this._renderTranscribedTextInto(translationEl, tengwarText);
          translationEl.style.display = '';
        } else {
          translationEl.textContent = '';
          translationEl.style.display = 'none';
        }
      }

      dispatchEngravingUpdate(side, text, font) {
        let displayText = text;
        if (font === 'Elvish' && text && typeof window.transcribe === 'function') {
          try {
            displayText = window.transcribe(text, {
              noAchLaut: true,
              medialOre: true,
              language: 'english',
            });
          } catch (e) {}
          // For consumers receiving the value via CustomEvent, deliver a
          // plain string (no markup) — they likely write it into textContent.
          displayText = this._stripElvishErrorMarkup(displayText);
        }
        document.dispatchEvent(
          new CustomEvent('ring-engraving-update', {
            detail: { side, text, displayText, font },
          })
        );
      }

      // ── Fit Indicator ──────────────────────────────────────────
      initFitIndicators() {
        if (typeof EngravingFitEngine === 'undefined') return;
        EngravingFitEngine.init().then(() => {
          this._fitReady = true;
          ['outside', 'inside'].forEach((side) => {
            const textarea = this.querySelector(`.engraving-form__textarea[data-side="${side}"]`);
            if (!textarea) return;
            if (this.querySelector(`.engraving-form__fit-indicator[data-fit-side="${side}"]`)) return;
            const indicator = document.createElement('div');
            indicator.className = 'engraving-form__fit-indicator';
            indicator.dataset.fitSide = side;
            indicator.style.display = 'none';
            textarea.parentNode.insertBefore(indicator, textarea.nextSibling);
          });
        });
      }

      updateFitIndicator(side, text, font) {
        if (!this._fitReady || typeof EngravingFitEngine === 'undefined') return;
        const indicator = this.querySelector(`.engraving-form__fit-indicator[data-fit-side="${side}"]`);
        const textarea = this.querySelector(`.engraving-form__textarea[data-side="${side}"]`);
        if (!indicator) return;

        if (!text || !text.trim()) {
          indicator.style.display = 'none';
          if (textarea) textarea.style.borderColor = '';
          return;
        }

        const result = EngravingFitEngine.calculateFit({
          text: text,
          font: font || 'Elvish',
          ringSize: this._readRingSize(),
          bandWidth: this._readBandWidth(),
          position: side,
          hasHallmark: side === 'inside',
        });

        const colors = { green: '#2a7d2a', amber: '#c48a1a', red: '#c42020' };
        const dotColor = colors[result.status] || '#888';

        indicator.innerHTML = `
          <span class="engraving-form__fit-dot" style="background:${dotColor};"></span>
          <span class="engraving-form__fit-text">${result.message}</span>
          <span class="engraving-form__fit-pct">${result.fillPct}%</span>
        `;
        indicator.style.display = '';

        if (textarea) textarea.style.borderColor = dotColor;
      }

      _readBandWidth() {
        // Try to read from a visible "Band Width" variant option on the PDP
        const opt = document.querySelector('[name^="options["][name*="and Width" i]:checked, [name^="options["][name*="andwidth" i]:checked');
        if (opt && opt.value) {
          const m = opt.value.match(/([\d.]+)\s*mm/i);
          if (m) return parseFloat(m[1]);
        }
        // Fallback: dataset.defaultBandWidth on the engraving-form element
        const def = parseFloat(this.dataset.defaultBandWidth);
        return Number.isFinite(def) ? def : 7.2;
      }

      _readRingSize() {
        const opt = document.querySelector('[name^="options["][name*="ize" i]:checked');
        if (opt && opt.value) {
          const m = opt.value.match(/(\d+(?:\.\d+)?)/);
          if (m) return parseFloat(m[1]);
        }
        const def = parseFloat(this.dataset.defaultRingSize);
        return Number.isFinite(def) ? def : 7;
      }
    }
  );
}
